import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Facebook, Mail, MessageCircle } from "lucide-react";
import { FadeUp, RevealLinesOnView } from "@/components/motion/Reveals";
import { useLang } from "@/i18n/LangContext";
import { useContent } from "@/cms/ContentContext";
import { useLeafTx } from "@/cms/useTexts";
import { useSpacing } from "@/cms/useSpacing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contacto = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const tx = useLeafTx();
  const { sectionPy, titleGap, overlineGap, divider } = useSpacing();
  const cmsChannels = content?.channels;
  const channels = {
    email: cmsChannels?.email || t.contacto.channels.email,
    whatsapp: cmsChannels?.whatsapp || t.contacto.channels.whatsapp,
    whatsappLabel: t.contacto.channels.whatsappLabel,
    instagram_url: cmsChannels?.instagram_url || "https://instagram.com/ordobyisa",
    facebook_url: cmsChannels?.facebook_url || "https://facebook.com/ordobyisa",
  };
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setStatus("ok");
      toast.success(t.contacto.form.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setStatus("idle"), 1200);
    } catch (err) {
      setStatus("error");
      toast.error(t.contacto.form.error);
      setTimeout(() => setStatus("idle"), 1500);
    }
  };

  return (
    <section id="contacto" data-testid="contacto-section" className={`relative ${sectionPy("contacto")} ${divider("bg-[#EAE5D9]/40")}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className={`overline overline-2x text-[#6B6554] ${overlineGap("contacto")}`}>{tx("contacto.overline", t.contacto.overline)}</div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-8 md:gap-16 mt-10 md:mt-14">
          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" className="col-span-12 lg:col-span-7 flex flex-col gap-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <FadeUp delay={0.05}>
                <label className="overline text-[#6B6554]">{t.contacto.form.name}</label>
                <input
                  data-testid="input-name"
                  className="ordo-field"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  required
                />
              </FadeUp>
              <FadeUp delay={0.1}>
                <label className="overline text-[#6B6554]">{t.contacto.form.email}</label>
                <input
                  data-testid="input-email"
                  type="email"
                  className="ordo-field"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  required
                />
              </FadeUp>
              <FadeUp delay={0.15}>
                <label className="overline text-[#6B6554]">{t.contacto.form.phone}</label>
                <input
                  data-testid="input-phone"
                  className="ordo-field"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </FadeUp>
              <FadeUp delay={0.2}>
                <label className="overline text-[#6B6554]">{t.contacto.form.service}</label>
                <select
                  data-testid="input-service"
                  className="ordo-field"
                  value={form.service}
                  onChange={(e) => set("service", e.target.value)}
                >
                  <option value="">—</option>
                  {t.contacto.form.serviceOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </FadeUp>
            </div>

            <FadeUp delay={0.25}>
              <div className="mt-6">
                <label className="overline text-[#6B6554]">{t.contacto.form.message}</label>
                <textarea
                  data-testid="input-message"
                  className="ordo-field"
                  rows={4}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  required
                />
              </div>
            </FadeUp>

            <FadeUp delay={0.35}>
              <motion.button
                data-testid="contact-submit"
                type="submit"
                disabled={status === "sending"}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="mt-10 inline-flex items-center justify-between gap-6 bg-[#3A3524] text-[#F5F2EC] px-8 py-5 uppercase tracking-[0.28em] text-[11px] hover:bg-[#8C714D] transition-colors duration-500 disabled:opacity-60 w-full md:w-auto"
              >
                <span>{status === "sending" ? t.contacto.form.sending : t.contacto.form.submit}</span>
                <span aria-hidden>{status === "sending" ? "…" : "→"}</span>
              </motion.button>
            </FadeUp>
          </form>

          {/* Channels */}
          <aside className="col-span-12 lg:col-span-5 flex flex-col justify-between gap-14">
            <div className="flex flex-col gap-8">
              <FadeUp delay={0.1}>
                <a
                  data-testid="channel-email"
                  href={`mailto:${channels.email}`}
                  className="group block border-t border-[#3A3524]/20 pt-4"
                >
                  <div className="overline text-[#6B6554]">Email</div>
                  <div className="font-montserrat-light text-2xl md:text-3xl text-[#3A3524] group-hover:text-[#8C714D] transition-colors">
                    {channels.email}
                  </div>
                </a>
              </FadeUp>
              <FadeUp delay={0.18}>
                <a
                  data-testid="channel-whatsapp"
                  href={`https://wa.me/${channels.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="group block border-t border-[#3A3524]/20 pt-4"
                >
                  <div className="overline text-[#6B6554]">{channels.whatsappLabel}</div>
                  <div className="font-montserrat-light text-2xl md:text-3xl text-[#3A3524] group-hover:text-[#8C714D] transition-colors">
                    {channels.whatsapp}
                  </div>
                </a>
              </FadeUp>
            </div>

            <FadeUp delay={0.28}>
              <div className="border-t border-[#3A3524]/20 pt-6">
                <div className="overline text-[#6B6554] mb-5">Social</div>
                <div className="flex items-center gap-4">
                  <a
                    data-testid="social-instagram"
                    href={channels.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-12 h-12 border border-[#3A3524]/40 flex items-center justify-center hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-colors duration-500"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} strokeWidth={1.4} />
                  </a>
                  <a
                    data-testid="social-facebook"
                    href={channels.facebook_url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-12 h-12 border border-[#3A3524]/40 flex items-center justify-center hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-colors duration-500"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} strokeWidth={1.4} />
                  </a>
                  <a
                    data-testid="social-whatsapp"
                    href={`https://wa.me/${channels.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-12 h-12 border border-[#3A3524]/40 flex items-center justify-center hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-colors duration-500"
                    aria-label="WhatsApp"
                  >
                    <MessageCircle size={18} strokeWidth={1.4} />
                  </a>
                  <a
                    data-testid="social-email"
                    href={`mailto:${channels.email}`}
                    className="w-12 h-12 border border-[#3A3524]/40 flex items-center justify-center hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-colors duration-500"
                    aria-label="Email"
                  >
                    <Mail size={18} strokeWidth={1.4} />
                  </a>
                </div>
              </div>
            </FadeUp>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Contacto;
